import { Children, isValidElement, ReactElement, ReactNode, useState } from 'react'
import { Box, Tab, Tabs, type TabsProps } from '@mui/material'

type PersistentTabProps<T extends string = string> = {
  label: string
  value: T
  children: ReactNode
}

export const PersistentTab = <T extends string>(_props: PersistentTabProps<T>): ReactElement => {
  return null as unknown as ReactElement
}

type PersistentTabsProps<T extends string> = {
  defaultValue: T
  children: ReactNode
} & Omit<TabsProps, 'value' | 'onChange'>

const PersistentTabs = <T extends string>({ defaultValue, children, ...tabsProps }: PersistentTabsProps<T>) => {
  const [value, setValue] = useState<T>(defaultValue)
  const [mountedTabs, setMountedTabs] = useState<Set<T>>(() => new Set([defaultValue]))

  const tabs: PersistentTabProps<T>[] = []
  Children.forEach(children, (child) => {
    if (isValidElement<PersistentTabProps<T>>(child) && child.type === PersistentTab) {
      tabs.push(child.props)
    }
  })

  const handleChange = (_: unknown, newValue: T) => {
    setMountedTabs((prev) => {
      if (prev.has(newValue)) return prev
      return new Set(prev).add(newValue)
    })
    setValue(newValue)
  }

  return (
    <>
      <Tabs value={value} onChange={handleChange} {...tabsProps}>
        {tabs.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>
      {tabs.map(
        (tab) =>
          mountedTabs.has(tab.value) && (
            <Box key={tab.value} sx={{ display: value === tab.value ? 'contents' : 'none' }}>
              {tab.children}
            </Box>
          )
      )}
    </>
  )
}

export default PersistentTabs
